import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { supabase } from '../../utils/supabase/client';
import {
  BookOpen, Users, Zap, BarChart3, Sparkles, Clock, Menu, X,
  ArrowRight, Play, GraduationCap, Keyboard, CheckCircle, Monitor, Smartphone,
  MessageSquareQuote, MessageCircle, Star
} from 'lucide-react';

type ClientFeedback = {
  id: string;
  display_name: string;
  category: string;
  user_type: string;
  rating: number;
  message: string;
  created_at: string;
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'teacher' | 'student'>('teacher');
  const [feedback, setFeedback] = useState<ClientFeedback[]>([]);
  const [feedbackPage, setFeedbackPage] = useState(0);
  const [feedbackError, setFeedbackError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadFeedback() {
      const { data, error } = await supabase
        .from('client_feedback')
        .select('id, display_name, category, user_type, rating, message, created_at')
        .order('created_at', { ascending: false })
        .limit(60);

      if (error) {
        console.error('Failed to load client feedback:', error);
        if (isMounted) setFeedbackError(true);
        return;
      }

      if (isMounted) {
        setFeedback(data ?? []);
        setFeedbackError(false);
      }
    }

    void loadFeedback();
    return () => {
      isMounted = false;
    };
  }, []);

  const feedbackPageCount = Math.ceil(feedback.length / 3);

  useEffect(() => {
    if (feedbackPageCount < 2) return;

    const intervalId = window.setInterval(() => {
      setFeedbackPage((currentPage) => (currentPage + 1) % feedbackPageCount);
    }, 10_000);

    return () => window.clearInterval(intervalId);
  }, [feedbackPageCount]);

  const visibleFeedback = feedback.slice(feedbackPage * 3, feedbackPage * 3 + 3);

  const features = [
    {
      icon: BookOpen,
      title: 'Interactive Lessons',
      description: 'Create engaging multimedia lessons with slides, videos, images, and 18+ activity types'
    },
    {
      icon: Users,
      title: 'Live & Self-Paced',
      description: 'Run real-time sessions or let students learn at their own pace with flexible modes'
    },
    {
      icon: Zap,
      title: 'Rich Activity Types',
      description: 'Quizzes, polls, drawing, collaboration boards, matching pairs, and gamified challenges'
    },
    {
      icon: BarChart3,
      title: 'Real-time Analytics',
      description: 'Track student engagement, participation, and performance with live dashboards'
    },
    {
      icon: Sparkles,
      title: 'Gamified Learning',
      description: 'Motivate students with competitive games like Time to Climb and Word Cloud'
    },
    {
      icon: Clock,
      title: 'Instant Session Codes',
      description: 'Students join in seconds with simple codes — no accounts or downloads needed'
    }
  ];

  const teacherBenefits = [
    'Create lessons with 18+ interactive activity types',
    'Run live sessions or assign self-paced work',
    'Track grades and analytics in real time',
    'Share lessons with other teachers',
    'Auto-grade quizzes and activities',
  ];

  const studentBenefits = [
    'Join sessions instantly with a simple code',
    'No account or download required',
    'Engage with interactive quizzes and games',
    'Get instant feedback on your work',
    'Learn at your own pace anytime',
  ];

  const steps = [
    {
      num: '1',
      title: 'Create a Lesson',
      description: 'Build interactive lessons with slides, videos, and activities',
      icon: BookOpen,
    },
    {
      num: '2',
      title: 'Start a Session',
      description: 'Launch live or self-paced sessions and get a unique code',
      icon: Play,
    },
    {
      num: '3',
      title: 'Students Join',
      description: 'Students enter the code — no accounts needed',
      icon: Keyboard,
    },
    {
      num: '4',
      title: 'Teach & Track',
      description: 'Monitor engagement and grades in real time',
      icon: BarChart3,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#e0e0e0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 lg:h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            >
              <img
                src="/edulive-logo.png"
                alt="EduLive"
                className="w-8 h-8 lg:w-9 lg:h-9 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-primary items-center justify-center hidden">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-base lg:text-lg font-semibold text-[#202124]">
                EduChemMix <span className="text-[#5f6368] font-normal text-sm">JHS</span>
              </span>
            </button>
            <nav className="hidden sm:flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-full px-4"
                onClick={() => navigate('/demos')}
              >
                Demos
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-full px-4"
                onClick={() => navigate('/join')}
              >
                Join Session
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-full px-4"
                onClick={() => navigate('/feedback')}
              >
                Feedback
              </Button>
              <Button
                size="sm"
                className="text-sm rounded-full px-5 ml-2"
                onClick={() => navigate('/teacher/login')}
              >
                Teacher Login
              </Button>
            </nav>
            <button
              className="sm:hidden p-2 rounded-full hover:bg-[#f1f3f4] text-[#5f6368] transition-colors"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="sm:hidden pb-4 border-t border-[#e0e0e0] pt-3">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => { navigate('/demos'); setIsMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-[#202124] hover:bg-[#f1f3f4] transition-colors"
                >
                  Demos
                </button>
                <button
                  onClick={() => { navigate('/join'); setIsMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-[#202124] hover:bg-[#f1f3f4] transition-colors"
                >
                  Join Session
                </button>
                <button
                  onClick={() => { navigate('/feedback'); setIsMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-[#202124] hover:bg-[#f1f3f4] transition-colors"
                >
                  Feedback
                </button>
                <button
                  onClick={() => { navigate('/teacher/login'); setIsMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
                >
                  Teacher Login
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-24 lg:py-28 px-4 sm:px-6 lg:px-8 bg-[#f8f9fa]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Interactive Classroom Platform for JHS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#202124] leading-tight tracking-tight">
              Make every lesson
              <span className="block text-primary mt-1">
                come alive
              </span>
            </h1>
            <p className="text-base sm:text-lg text-[#5f6368] mt-5 sm:mt-6 max-w-2xl mx-auto leading-relaxed">
              Transform your classroom with real-time interactive lessons, quizzes, 
              games, and analytics. Students join instantly — no accounts needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 sm:mt-10">
              <Button
                size="lg"
                className="h-12 px-7 rounded-full text-sm font-medium shadow-sm"
                onClick={() => navigate('/teacher/login')}
              >
                Get started — it's free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-7 rounded-full text-sm font-medium border-[#dadce0] text-[#3c4043] hover:bg-[#f1f3f4]"
                onClick={() => navigate('/join')}
              >
                <Keyboard className="w-4 h-4 mr-2" />
                Join a session
              </Button>
            </div>
          </div>

          {/* Platform Preview */}
          <div className="mt-14 sm:mt-20 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 h-10 border-b border-[#e0e0e0] bg-[#f8f9fa]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ed6a5e]" />
                  <div className="w-3 h-3 rounded-full bg-[#f5bf4f]" />
                  <div className="w-3 h-3 rounded-full bg-[#62c554]" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white rounded-md px-4 py-0.5 text-xs text-[#5f6368] border border-[#e0e0e0]">
                    edulive.fun
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#e0e0e0]">
                <div className="p-5 sm:p-6 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#202124]">Create Lessons</p>
                  <p className="text-[10px] sm:text-xs text-[#5f6368] mt-1">Slides + Activities</p>
                </div>
                <div className="p-5 sm:p-6 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-5 h-5 text-accent" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#202124]">Live Sessions</p>
                  <p className="text-[10px] sm:text-xs text-[#5f6368] mt-1">Real-time interaction</p>
                </div>
                <div className="p-5 sm:p-6 text-center">
                  <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="w-5 h-5 text-orange" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#202124]">Track Progress</p>
                  <p className="text-[10px] sm:text-xs text-[#5f6368] mt-1">Grades & analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#202124] tracking-tight">
              How it works
            </h2>
            <p className="text-sm sm:text-base text-[#5f6368] mt-3 max-w-xl mx-auto">
              Get up and running in minutes — no training required
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center group">
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[60%] w-[80%] border-t-2 border-dashed border-[#e0e0e0]" />
                )}
                <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/8 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <step.icon className="w-6 h-6" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-[10px] font-bold text-white flex items-center justify-center">
                    {step.num}
                  </span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-[#202124] mb-1.5">
                  {step.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#5f6368] leading-relaxed max-w-[200px] mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#f8f9fa]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#202124] tracking-tight">
              Everything you need for interactive learning
            </h2>
            <p className="text-sm sm:text-base text-[#5f6368] mt-3 max-w-xl mx-auto">
              Powerful tools designed for modern JHS educators
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-[#e0e0e0] hover:border-primary/30 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/12 transition-colors">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-[#202124] mb-1.5">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#5f6368] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teacher / Student Tabs */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#202124] tracking-tight">
              Built for teachers and students
            </h2>
            <p className="text-sm sm:text-base text-[#5f6368] mt-3 max-w-xl mx-auto">
              A seamless experience for everyone in the classroom
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-8 sm:mb-10">
            <div className="inline-flex bg-[#f1f3f4] rounded-full p-1">
              <button
                onClick={() => setActiveTab('teacher')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'teacher'
                    ? 'bg-white text-[#202124] shadow-sm'
                    : 'text-[#5f6368] hover:text-[#202124]'
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                For Teachers
              </button>
              <button
                onClick={() => setActiveTab('student')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'student'
                    ? 'bg-white text-[#202124] shadow-sm'
                    : 'text-[#5f6368] hover:text-[#202124]'
                }`}
              >
                <Users className="w-4 h-4" />
                For Students
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-[#202124] mb-2">
                {activeTab === 'teacher'
                  ? 'Create engaging lessons in minutes'
                  : 'Join and learn from anywhere'}
              </h3>
              <p className="text-sm sm:text-base text-[#5f6368] mb-6 leading-relaxed">
                {activeTab === 'teacher'
                  ? 'Build interactive lessons with our drag-and-drop editor, launch sessions with a single click, and track every student\'s progress in real time.'
                  : 'Enter a simple session code to join your teacher\'s lesson. Participate in quizzes, games, and activities right from your browser — no sign-up needed.'}
              </p>
              <ul className="space-y-3">
                {(activeTab === 'teacher' ? teacherBenefits : studentBenefits).map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#202124]">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button
                  className="rounded-full h-11 px-6 text-sm font-medium"
                  onClick={() => navigate(activeTab === 'teacher' ? '/teacher/login' : '/join')}
                >
                  {activeTab === 'teacher' ? 'Start teaching' : 'Join a session'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="bg-[#f8f9fa] rounded-2xl border border-[#e0e0e0] p-8 sm:p-10 w-full max-w-sm">
                {activeTab === 'teacher' ? (
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Monitor className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#202124]">Dashboard</p>
                        <p className="text-xs text-[#5f6368]">Manage all your lessons</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#202124]">18+ Activities</p>
                        <p className="text-xs text-[#5f6368]">Quiz, Poll, Draw & more</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-orange" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#202124]">Live Analytics</p>
                        <p className="text-xs text-[#5f6368]">Track grades in real time</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="flex items-center justify-center gap-2">
                      <Smartphone className="w-5 h-5 text-[#5f6368]" />
                      <span className="text-xs text-[#5f6368]">Works on any device</span>
                    </div>
                    <div className="bg-white rounded-xl border border-[#dadce0] p-5">
                      <p className="text-xs text-[#5f6368] mb-3">Enter session code</p>
                      <div className="bg-[#f1f3f4] rounded-lg h-10 flex items-center justify-center font-mono text-lg tracking-[0.3em] text-[#202124]">
                        ABC123
                      </div>
                    </div>
                    <p className="text-xs text-[#5f6368]">
                      That's it — you're in!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Feedback */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#f8f9fa]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 text-primary text-sm font-medium mb-3">
                <MessageSquareQuote className="w-4 h-4" />
                From our community
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#202124] tracking-tight">
                Client Feedback
              </h2>
              <p className="text-sm sm:text-base text-[#5f6368] mt-3 max-w-xl">
                Suggestions and experiences shared by the EduChemMix community.
              </p>
            </div>
            <Button
              className="rounded-full h-11 px-6 text-sm font-medium self-start sm:self-auto"
              onClick={() => navigate('/feedback')}
            >
              Share your feedback
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {feedbackError ? (
            <div className="rounded-xl border border-[#e0e0e0] bg-white p-8 text-center">
              <p className="text-sm text-[#5f6368]">
                Feedback could not be loaded right now. Please try again later.
              </p>
            </div>
          ) : feedback.length === 0 ? (
            <div className="rounded-xl border border-[#e0e0e0] bg-white p-8 text-center">
              <MessageCircle className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-sm font-medium text-[#202124]">Be the first to share feedback</p>
              <p className="text-sm text-[#5f6368] mt-1">
                Your suggestion or experience can help improve EduChemMix.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleFeedback.map((item) => (
                  <article
                    key={item.id}
                    className="flex flex-col bg-white rounded-xl p-6 border border-[#e0e0e0] min-h-52"
                  >
                    <div
                      className="flex items-center gap-1 text-amber-500 mb-3"
                      role="img"
                      aria-label={`${item.rating} out of 5 stars`}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= item.rating ? 'fill-current' : ''}`}
                          aria-hidden="true"
                        />
                      ))}
                      <span className="ml-1 text-xs text-[#5f6368]">{item.rating}/5</span>
                    </div>
                    <div className="text-primary mb-4" aria-hidden="true">
                      <MessageSquareQuote className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-[#3c4043] leading-relaxed flex-1 whitespace-pre-wrap">
                      “{item.message}”
                    </p>
                    <div className="mt-5 pt-4 border-t border-[#f1f3f4] flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-[#202124]">{item.display_name}</p>
                        <p className="text-xs text-[#5f6368] mt-0.5">
                          {new Date(item.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className="text-[11px] font-medium text-primary bg-primary/8 rounded-full px-2.5 py-1">
                          {item.category}
                        </span>
                        <span className="text-[11px] text-[#5f6368]">{item.user_type}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              {feedbackPageCount > 1 && (
                <div className="flex justify-center items-center gap-2 mt-7" aria-label="Feedback pages">
                  {Array.from({ length: feedbackPageCount }, (_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Show feedback group ${index + 1}`}
                      aria-current={feedbackPage === index ? 'true' : undefined}
                      onClick={() => setFeedbackPage(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        feedbackPage === index ? 'bg-primary' : 'bg-[#c4c7c5] hover:bg-[#80868b]'
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#f8f9fa]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-sm px-8 py-12 sm:px-12 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#202124] mb-3">
              Ready to transform your classroom?
            </h2>
            <p className="text-sm sm:text-base text-[#5f6368] mb-8 max-w-lg mx-auto">
              Join educators who are making learning interactive and engaging every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="h-11 px-7 rounded-full text-sm font-medium"
                onClick={() => navigate('/teacher/login')}
              >
                Get started free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-11 px-7 rounded-full text-sm font-medium border-[#dadce0] text-[#3c4043] hover:bg-[#f1f3f4]"
                onClick={() => navigate('/demos')}
              >
                <Play className="w-4 h-4 mr-2" />
                Explore demos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#e0e0e0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/edulive-logo.png"
                alt="EduLive"
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="w-7 h-7 rounded-md bg-primary items-center justify-center hidden">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-[#5f6368]">
                EduChemMix - JHS
              </span>
            </div>
            <div className="flex items-center gap-6 text-xs text-[#5f6368]">
              <button onClick={() => navigate('/demos')} className="hover:text-[#202124] transition-colors">Demos</button>
              <button onClick={() => navigate('/join')} className="hover:text-[#202124] transition-colors">Join Session</button>
              <button onClick={() => navigate('/feedback')} className="hover:text-[#202124] transition-colors">Feedback</button>
              <button onClick={() => navigate('/teacher/login')} className="hover:text-[#202124] transition-colors">Teacher Login</button>
            </div>
            <p className="text-xs text-[#9aa0a6]">
              &copy; 2026 EduChemMix-JHS. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}